import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { UserCommunication } from '@this+that/models';

type ListItem = Pick<UserCommunication, 'messageId' | 'createdAt'>;

interface UseEmailListScrollTopProps {
  container: HTMLElement | null;
  visibleCommunications: ListItem[];
  resetKey?: string;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const OVERFLOW_ANCHOR_PROPERTY = 'overflow-anchor';
const OVERFLOW_ANCHOR_AUTO = 'auto';
const SCROLL_EVENT = 'scroll';
const SCROLL_BEHAVIOR_SMOOTH = 'smooth';
const SCROLL_BEHAVIOR_INSTANT = 'auto';

const isPastFirstScreen = (container: HTMLElement) =>
  container.scrollTop > container.clientHeight;

const supportsOverflowAnchor = () =>
  typeof CSS === 'undefined' ||
  typeof CSS.supports !== 'function' ||
  CSS.supports(OVERFLOW_ANCHOR_PROPERTY, OVERFLOW_ANCHOR_AUTO);

const prefersReducedMotion = () =>
  window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;

function useIsPastFirstScreen(container: HTMLElement | null) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!container) {
      setVisible(false);
      return;
    }
    const update = () => setVisible(isPastFirstScreen(container));
    update();
    container.addEventListener(SCROLL_EVENT, update, { passive: true });
    return () => container.removeEventListener(SCROLL_EVENT, update);
  }, [container]);

  return visible;
}

function useNewEmailCount(
  visible: boolean,
  list: ListItem[],
  resetKey: string | undefined,
) {
  const [anchor, setAnchor] = useState<number | null>(null);
  const listRef = useRef(list);
  listRef.current = list;

  useEffect(() => {
    setAnchor(visible ? (listRef.current[0]?.createdAt ?? null) : null);
  }, [visible]);

  useEffect(() => {
    setAnchor(visible ? (listRef.current[0]?.createdAt ?? null) : null);
  }, [resetKey]);

  if (anchor === null) {
    return 0;
  }
  return list.filter((item) => item.createdAt > anchor).length;
}

function useScrollCompensation(
  container: HTMLElement | null,
  firstMessageId: string | undefined,
  resetKey: string | undefined,
) {
  const previous = useRef<{ id?: string; height: number } | null>(null);
  const lastResetKey = useRef(resetKey);

  useLayoutEffect(() => {
    if (!container || supportsOverflowAnchor()) {
      return;
    }
    if (lastResetKey.current !== resetKey) {
      lastResetKey.current = resetKey;
      previous.current = null;
    }
    const last = previous.current;
    const changed = last !== null && last.id !== firstMessageId;
    if (changed && isPastFirstScreen(container)) {
      container.scrollTop += container.scrollHeight - last.height;
    }
    previous.current = { id: firstMessageId, height: container.scrollHeight };
  });
}

export function useEmailListScrollTop({
  container,
  visibleCommunications,
  resetKey,
}: Readonly<UseEmailListScrollTopProps>) {
  const visible = useIsPastFirstScreen(container);
  const newCount = useNewEmailCount(visible, visibleCommunications, resetKey);
  useScrollCompensation(
    container,
    visibleCommunications[0]?.messageId,
    resetKey,
  );

  const scrollToTop = useCallback(() => {
    container?.scrollTo({
      top: 0,
      behavior: prefersReducedMotion()
        ? SCROLL_BEHAVIOR_INSTANT
        : SCROLL_BEHAVIOR_SMOOTH,
    });
  }, [container]);

  return { visible, newCount, scrollToTop };
}
